import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Lock, KeyRound, ShieldCheck, Loader2, Eye, EyeOff } from "lucide-react";

export const ChangePasswordForm = () => {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => setShow({ ...show, [k]: !show[k] });

  const strength = (() => {
    const p = form.next;
    if (!p) return { score: 0, label: "" };
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return { score: s, label: ["Lemah", "Sederhana", "Baik", "Kuat"][Math.max(0, s - 1)] || "Lemah" };
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return toast.error("Password baru minimum 8 aksara");
    if (form.next !== form.confirm) return toast.error("Pengesahan password tidak sepadan");
    if (form.current === form.next) return toast.error("Password baru mesti berbeza");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password berjaya ditukar. Sila log masuk semula.");
      // Redirect BEFORE clearing auth to avoid AdminDashboard's Navigate guard racing us
      setTimeout(() => {
        localStorage.removeItem("azmiey_token");
        window.location.replace("/login");
      }, 1200);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal tukar password");
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-6 max-w-xl">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 grid place-items-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-violet-400" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold">Tukar Password</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Log masuk sebagai <span className="text-zinc-300">{user?.email}</span></p>
        </div>
      </div>

      <form onSubmit={submit} data-testid="change-password-form" className="grid gap-4">
        <PwdField
          testid="cp-current-input"
          Icon={Lock}
          label="Password Semasa"
          value={form.current}
          onChange={set("current")}
          show={show.current}
          onToggle={() => toggle("current")}
          autoComplete="current-password"
        />

        <div>
          <PwdField
            testid="cp-new-input"
            Icon={KeyRound}
            label="Password Baru (min 8 aksara)"
            value={form.next}
            onChange={set("next")}
            show={show.next}
            onToggle={() => toggle("next")}
            autoComplete="new-password"
          />
          {form.next && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 flex gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      i <= strength.score
                        ? strength.score === 1
                          ? "bg-red-500"
                          : strength.score === 2
                          ? "bg-amber-500"
                          : strength.score === 3
                          ? "bg-emerald-500"
                          : "bg-violet-500"
                        : "bg-white/5"
                    }`}
                  />
                ))}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{strength.label}</span>
            </div>
          )}
        </div>

        <PwdField
          testid="cp-confirm-input"
          Icon={KeyRound}
          label="Sahkan Password Baru"
          value={form.confirm}
          onChange={set("confirm")}
          show={show.confirm}
          onToggle={() => toggle("confirm")}
          autoComplete="new-password"
        />

        {form.confirm && form.confirm !== form.next && (
          <p className="text-xs text-red-400 -mt-2">Password tidak sepadan</p>
        )}

        <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3 text-[11px] text-amber-200/90 leading-relaxed">
          <strong className="text-amber-300">Nota keselamatan:</strong> Password akan di-hash guna bcrypt. Anda akan log keluar dan perlu log masuk semula selepas tukar.
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="cp-submit-btn"
          className="h-11 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-60 font-semibold flex items-center justify-center gap-2 purple-glow"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {submitting ? "Menukar..." : "Tukar Password"}
        </button>
      </form>
    </div>
  );
};

const PwdField = ({ testid, Icon, label, value, onChange, show, onToggle, autoComplete }) => (
  <label className="block">
    <span className="text-xs text-zinc-400 mb-1 block">{label}</span>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 z-10" />
      <Input
        data-testid={testid}
        type={show ? "text" : "password"}
        required
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className="pl-10 pr-10 h-11 rounded-xl bg-[#121214] border-white/10 focus-visible:ring-violet-500 focus-visible:border-violet-500 text-white"
        placeholder="••••••••"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </label>
);

export default ChangePasswordForm;
